// src/components/customers/ColoredCommentsEditor.jsx
import { useLayoutEffect, useRef, useState } from "react";
import {
  COLOR_CHOICES,
  applyColorToRange,
  applyTextEdit,
  colorClassName,
  isValidSegments,
  normalizeSegments,
  replaceRange,
  segmentsForStorage,
  segmentsToText,
  textToSegments,
} from "../../utils/coloredText";

/**
 * ColoredCommentsEditor
 *
 * The "Comments & Notes" field in the Add / Edit Measurement popup, with the
 * ability to colour a *selected part* of the note red, green or orange.
 *
 * HOW IT WORKS — and why it is not a rich-text editor:
 * A real <textarea> stays in charge of everything (typing, caret, selection,
 * undo, IME, mobile keyboards, the 1000-char cap). Its own text is rendered
 * transparent, and a pixel-aligned read-only backdrop sitting directly behind
 * it paints the same characters in colour. So the user is always editing plain
 * text — there is no contenteditable, no HTML in the document, and nothing
 * that could inject markup into Firestore. Colour is metadata stored beside the
 * string (`commentsSegments`), never inside it.
 *
 * Consequence to preserve if you touch the styling: the textarea and the
 * backdrop MUST keep identical box metrics (same padding, border width,
 * font-size, line-height, wrapping) or the colours will drift off the words.
 *
 * The component is seeded from props ON MOUNT only. That is safe because
 * RoomModal returns null while closed, so this remounts fresh for every
 * Add/Edit the user opens.
 *
 * @param {Object} props
 * @param {string} props.id - Field id, matched by the parent's label htmlFor
 * @param {string} props.value - Initial plain note (`params.comments`)
 * @param {Array} [props.segments] - Initial `params.commentsSegments`
 * @param {Function} props.onChange - Called as onChange({ target: { id, value, segments } })
 * @param {number} [props.maxLength] - Plain-character cap
 * @param {number} [props.rows] - Textarea rows
 * @param {string} [props.placeholder]
 */
const ColoredCommentsEditor = ({
  id = "comments",
  value = "",
  segments: initialSegments,
  onChange,
  maxLength = 1000,
  rows = 8,
  placeholder = "",
}) => {
  const textareaRef = useRef(null);
  const backdropRef = useRef(null);

  // Selection to restore after a controlled re-render (colour apply / paste).
  const pendingSelectionRef = useRef(null);

  const [hasSelection, setHasSelection] = useState(false);

  // Seed once. Trust the incoming segments only if they still join back to the
  // stored note — otherwise treat the note as plain (see isValidSegments).
  const [segments, setSegments] = useState(() =>
    isValidSegments(initialSegments, value || "")
      ? normalizeSegments(initialSegments)
      : textToSegments(value || "")
  );

  const text = segmentsToText(segments);

  /**
   * Push a new segment array to local state and up to the form.
   * `comments` is always the live join, so the two can never drift.
   */
  const commit = (next) => {
    const normalized = normalizeSegments(next);
    setSegments(normalized);

    onChange({
      target: {
        id,
        value: segmentsToText(normalized),
        segments: segmentsForStorage(normalized),
      },
    });
  };

  // Fold the textarea's own edit (type / delete / cut / undo) into the segments.
  //
  // Known limitation: dragging a selection to a new spot INSIDE the field is
  // two edits at once, and the before/after diff can only recover one range —
  // colours across the moved span may reset to plain. The text itself is always
  // correct, which is the invariant that matters; colouring is re-appliable.
  const handleChange = (event) => {
    const next = event.target.value;
    if (next === text) return;
    commit(applyTextEdit(segments, text, next));
  };

  /**
   * Force paste to plain text. The textarea could not accept markup anyway,
   * but reading text/plain explicitly means a rich-text clipboard payload is
   * flattened rather than partially honoured, and lets us enforce the
   * character cap that maxLength can't apply once we preventDefault.
   */
  const handlePaste = (event) => {
    event.preventDefault();

    const textarea = event.target;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    const plain = (event.clipboardData?.getData("text/plain") || "").replace(
      /\r\n?/g,
      "\n"
    );

    const remaining = maxLength - (text.length - (end - start));
    const inserted = remaining > 0 ? plain.slice(0, remaining) : "";

    commit(replaceRange(segments, start, end, inserted));

    // Paste collapses the selection to a caret after the inserted text.
    const caret = start + inserted.length;
    pendingSelectionRef.current = [caret, caret];
    setHasSelection(false);
  };

  // Colour (or clear) whatever the user currently has selected.
  const applyColor = (color) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    if (start === end) return;

    // Keep the selection so the user can try another colour immediately —
    // hasSelection therefore stays true and needs no update here.
    commit(applyColorToRange(segments, start, end, color));
    pendingSelectionRef.current = [start, end];
  };

  const syncSelection = (event) => {
    setHasSelection(event.target.selectionStart !== event.target.selectionEnd);
  };

  // Keep the backdrop scrolled in lockstep with the textarea.
  const syncScroll = () => {
    const textarea = textareaRef.current;
    const backdrop = backdropRef.current;
    if (!textarea || !backdrop) return;

    backdrop.scrollTop = textarea.scrollTop;
    backdrop.scrollLeft = textarea.scrollLeft;
  };

  // Runs after every render. Restores the caret/selection that a colour apply
  // or a paste asked for (the controlled re-render would otherwise drop it),
  // and keeps the backdrop aligned. Deliberately sets no state.
  useLayoutEffect(() => {
    syncScroll();

    const range = pendingSelectionRef.current;
    if (!range) return;

    pendingSelectionRef.current = null;

    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.focus();
    textarea.setSelectionRange(range[0], range[1]);
  });

  return (
    <div>
      {/* Colour toolbar — active only while part of the note is selected,
          because a colour with nothing selected has nothing to apply to. */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="text-sm text-gray-500 mr-1">Text colour:</span>

        {COLOR_CHOICES.map((choice) => (
          <button
            key={choice.value}
            type="button"
            // Stop the textarea losing focus (and the selection with it).
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => applyColor(choice.value)}
            disabled={!hasSelection}
            className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border transition-all duration-200 ${
              hasSelection
                ? "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:shadow-sm cursor-pointer"
                : "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
            }`}
            title={
              hasSelection
                ? `Colour the selected text ${choice.label.toLowerCase()}`
                : "Select some text first"
            }
          >
            <span
              className={`w-3 h-3 rounded-full mr-2 ${choice.swatch} ${
                hasSelection ? "" : "opacity-40"
              }`}
              aria-hidden="true"
            />
            {choice.label}
          </button>
        ))}

        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => applyColor(null)}
          disabled={!hasSelection}
          className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border transition-all duration-200 ${
            hasSelection
              ? "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:shadow-sm cursor-pointer"
              : "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
          }`}
          title={
            hasSelection
              ? "Remove colour from the selected text"
              : "Select some text first"
          }
        >
          Clear colour
        </button>
      </div>

      {/* Overlay pair. The wrapper is sized by the textarea (which stays in
          normal flow), so dragging the textarea's resize handle keeps the
          backdrop the same size automatically. */}
      <div className="relative">
        <div
          ref={backdropRef}
          aria-hidden="true"
          className="pointer-events-none select-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words rounded-lg border border-transparent px-4 py-3 text-base"
        >
          {segments.map((segment, index) => (
            <span key={index} className={colorClassName(segment.color)}>
              {segment.text}
            </span>
          ))}
          {/* Trailing newline so a note ending in Enter still scrolls right. */}
          {"\n"}
        </div>

        <textarea
          ref={textareaRef}
          id={id}
          value={text}
          onChange={handleChange}
          onPaste={handlePaste}
          onSelect={syncSelection}
          onBlur={() => setHasSelection(false)}
          onScroll={syncScroll}
          rows={rows}
          maxLength={maxLength}
          placeholder={placeholder}
          className="relative z-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 resize-y text-base bg-transparent text-transparent caret-gray-900 placeholder:text-gray-400"
        />
      </div>
    </div>
  );
};

export default ColoredCommentsEditor;
